import { mcpCallFailed, contextualMcpCall } from './mcpCallResult.js';
import { browserReadPlan, isWebUrl } from './browserReadPlan.js';
// Apolo IDE — web_browse: the agent's read-only window onto the live web (apolo-improver E0).
//
// THE GAP IT CLOSES: a Hermes/Radar agent can already WRITE findings
// (radar_emit) and READ Radar state (radar_query), but it had no first-class
// way to actually LOOK at the open web — to load a repo's README, a docs page,
// or a logged-in X/IG/TikTok/Skool feed — through the user's own machine. This
// tool is that window. It runs in the RENDERER, where `window.apolo` exposes
// three browser surfaces, and it routes to the cheapest one that can do the job:
//
//   login:true                 → window.apolo.browser.chromeMcp.call(...)   the
//     user's REAL logged-in Chrome (chrome-devtools-mcp). The only path that
//     sees authed pages (X / Instagram / TikTok / Skool).
//   login falsey + needs JS    → window.apolo.browser.embedded.navigate()
//     then .read({selector}). The in-app BrowserView (APP session, runs JS).
//   simplest public            → window.apolo.fetch.url({url})              a
//     plain server-side GET (no session, no JS). The default for a raw url.
//   query (no url)             → build a search URL, then read it like a url.
//
// Like radar_emit/radar_query it NEVER throws: every outcome — success, a
// missing sub-api, a navigation failure — comes back as a STRING for the loop's
// tool-result contract. Output is CAPPED (8000 chars / 40 links) so one page
// can't blow the small local model's context window. Read-only ⇒
// `mutating: false` (no approval gate).
//
// Injected into EVERY Hermes run via the agentLoop `extraTools` seam by
// appExecutionAdapter (beside radarEmitTool/radarQueryTool). `apolo` is
// injectable so the unit tests run without a real renderer.

const MAX_CHARS = 8000;
const MAX_LINKS = 40;

export const WEB_BROWSE_DEF = {
  type: 'function',
  function: {
    name: 'web_browse',
    description:
      'Abre a web (somente leitura) pela máquina do usuário — use isto pra LER um repo/README/docs/post; nunca curl/fetch manual. ' +
      'url: a página a carregar. query (sem url): faz uma busca e lê os resultados. ' +
      'login=true: usa o Chrome REAL logado do usuário (X/Instagram/TikTok/Skool — único jeito de ver feed autenticado). ' +
      'extract="text" (padrão) devolve o texto da página; extract="links" devolve os links. ' +
      'selector: limita a leitura a um trecho (CSS). Retorna texto/links CAPADOS + a url de origem. ' +
      'Ex: {"url":"https://github.com/anthropics/skills","extract":"text"} ou {"query":"claude agent skills github","extract":"links"} ou {"url":"https://x.com/anthropicai","login":true}',
    parameters: {
      type: 'object',
      properties: {
        url: { type: 'string', description: 'a página a abrir (http/https)' },
        query: { type: 'string', description: 'busca quando não há url — vira uma URL de busca' },
        login: { type: 'boolean', description: 'true = Chrome real logado (páginas autenticadas)' },
        extract: { type: 'string', enum: ['text', 'links'], description: 'o que devolver (padrão text)' },
        selector: { type: 'string', description: 'CSS para limitar a leitura a um trecho' },
      },
    },
  },
};

// A search with no url → a public results page the agent can then read. Google
// is the broadest; the agent narrows with site: in its query if it wants.
function searchUrl(query) {
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}

// Cap free text so a long page never blows the context window. Appends a clear
// marker when it actually truncated so the model knows there is more.
function capText(text) {
  const s = String(text == null ? '' : text);
  if (s.length <= MAX_CHARS) return s;
  return `${s.slice(0, MAX_CHARS)}\n[…truncado em ${MAX_CHARS} chars de ${s.length}]`;
}

// Pull links out of a chunk of HTML (embedded read with include_html) — cheap
// regex, deduped, capped. Used only for extract:'links'.
function linksFromHtml(html, baseUrl) {
  const out = [];
  const seen = new Set();
  const re = /<a\b[^>]*\bhref\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi;
  let m;
  while ((m = re.exec(html)) && out.length < MAX_LINKS) {
    let href = m[1].trim();
    if (!href || href.startsWith('#') || href.startsWith('javascript:')) continue;
    try {
      href = baseUrl ? new URL(href, baseUrl).href : href;
    } catch {
      // keep the raw href if it can't be resolved against the base
    }
    if (seen.has(href)) continue;
    seen.add(href);
    const label = m[2].replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    out.push(label ? `${label} — ${href}` : href);
  }
  return out;
}

// chrome-devtools-mcp returns JSON-RPC results; a tools/call surfaces the page
// as { content: [{ type:'text', text }] }. Flatten any text we can find so the
// model gets the page even if the exact result shape shifts between versions.
function textFromMcpResult(result) {
  if (result == null) return '';
  if (typeof result === 'string') return result;
  if (Array.isArray(result.content)) {
    return result.content
      .map((c) => (typeof c === 'string' ? c : c && typeof c.text === 'string' ? c.text : ''))
      .filter(Boolean)
      .join('\n');
  }
  if (typeof result.text === 'string') return result.text;
  if (typeof result.snapshot === 'string') return result.snapshot;
  try {
    return JSON.stringify(result);
  } catch {
    return '';
  }
}

/**
 * Build the web_browse extra-tool for the agentLoop seam.
 * @param {{ apolo?: object }} deps  injectable renderer bridge (defaults to window.apolo)
 * @returns {{ def: object, execute: (args: object) => Promise<string>, mutating: false }}
 */
export function makeWebBrowseTool({ apolo } = {}) {
  // Resolve lazily-bound default each call so a renderer that exposes
  // window.apolo AFTER construction still works (preload timing is async).
  const resolveApolo = () =>
    apolo || (typeof window !== 'undefined' ? window.apolo : undefined) || undefined;

  // login:true — drive the user's real logged-in Chrome via chrome-devtools-mcp.
  // We navigate, then ask for the page text; result text is capped. Any miss is
  // a graceful string (and the caller may retry without login).
  async function viaChromeMcp(bridge, url, extract, selector) {
    const nav = await bridge.call({ method: 'navigate_page', params: { url } });
    if (mcpCallFailed(nav)) {
      return `Erro web_browse: Chrome logado indisponível (${nav?.reason || 'falha ao navegar'}). Tente sem login=true.`;
    }
    const res = await bridge.call(contextualMcpCall(browserReadPlan(extract, selector), nav.contextVersion));
    if (mcpCallFailed(res)) {
      // Some chrome-devtools-mcp builds only ship a text snapshot — fall back
      // to it rather than failing the whole browse.
      const snap = await bridge.call(contextualMcpCall({ method: 'take_snapshot', params: {} }, nav.contextVersion));
      const snapText = !mcpCallFailed(snap) ? textFromMcpResult(snap.result) : '';
      if (snapText) return `Fonte (Chrome logado): ${url}\n\n${capText(snapText)}`;
      return `Erro web_browse: Chrome logado não retornou conteúdo (${res?.reason || 'sem resultado'}). Tente sem login=true.`;
    }
    const text = textFromMcpResult(res ? res.result : null);
    if (!text) return `Erro web_browse: Chrome logado abriu ${url} mas não retornou texto. Tente sem login=true.`;
    return `Fonte (Chrome logado): ${url}\n\n${capText(text)}`;
  }

  // login falsey + needs JS — the in-app BrowserView (APP session). navigate()
  // then read({selector}). extract:'links' asks for include_html and scrapes.
  async function viaEmbedded(embedded, url, extract, selector) {
    // ssrfGuard=true — this is the autonomous-agent path; block loopback/IMDS/LAN
    // (the preview engine calls navigate WITHOUT the flag, so localhost still loads).
    const nav = await embedded.navigate({ url, ssrfGuard: true });
    if (nav && nav.ok === false) {
      return { ok: false, reason: nav.reason || 'falha ao navegar' };
    }
    const read = await embedded.read({ selector, include_html: extract === 'links' });
    if (!read || read.ok === false) {
      return { ok: false, reason: (read && read.reason) || 'falha ao ler a página' };
    }
    const source = read.url || (nav && nav.url) || url;
    if (extract === 'links') {
      const links = linksFromHtml(read.html || '', source);
      const body = links.length ? links.join('\n') : '(nenhum link encontrado)';
      return { ok: true, text: `Fonte: ${source}\n\n${body}` };
    }
    return { ok: true, text: `Fonte: ${source}\n\n${capText(read.text)}` };
  }

  // simplest public — a plain server-side GET (no session, no JS). text only;
  // a links request can't be honored here (no HTML parse), so we say so.
  async function viaFetch(fetchApi, url, extract) {
    const res = await fetchApi.url({ url, maxChars: MAX_CHARS });
    if (!res || res.ok === false) {
      return { ok: false, reason: (res && res.error) || 'falha no fetch' };
    }
    const source = res.finalUrl || res.url || url;
    if (extract === 'links') {
      return {
        ok: true,
        text: `Fonte: ${source}\n\n(fetch simples não extrai links — repita sem login para usar o navegador embutido, que lê o HTML)\n\n${capText(res.text)}`,
      };
    }
    const trunc = res.truncated ? `\n[…truncado em ${res.chars || MAX_CHARS} chars]` : '';
    return { ok: true, text: `Fonte: ${source}\n\n${capText(res.text)}${trunc}` };
  }

  async function execute(args = {}) {
    try {
      const extract = args.extract === 'links' ? 'links' : 'text';
      const selector = typeof args.selector === 'string' && args.selector.trim() ? args.selector.trim() : undefined;
      const rawUrl = typeof args.url === 'string' ? args.url.trim() : '';
      const query = typeof args.query === 'string' ? args.query.trim() : '';

      if (!rawUrl && !query) {
        return 'Erro web_browse: informe url ou query.';
      }
      const url = rawUrl || searchUrl(query);
      if (!isWebUrl(url)) return 'Erro web_browse: apenas endereços HTTP ou HTTPS são permitidos.';

      const a = resolveApolo();
      if (!a || !a.browser) {
        return `Erro web_browse: navegador indisponível (window.apolo ausente — rode no app). url pretendida: ${url}`;
      }

      // login:true — the user's real Chrome. If the bridge is missing, fall
      // through to the embedded/fetch path with a clear note (never hard-fail).
      if (args.login) {
        const bridge = a.browser.chromeMcp;
        if (bridge && typeof bridge.call === 'function') {
          return await viaChromeMcp(bridge, url, extract, selector);
        }
        // graceful fallback: try the embedded view, telling the model the
        // authed content may be missing without the real Chrome.
        if (a.browser.embedded && typeof a.browser.embedded.navigate === 'function') {
          const emb = await viaEmbedded(a.browser.embedded, url, extract, selector);
          if (emb.ok) {
            return `Aviso web_browse: Chrome logado indisponível — usei o navegador embutido (pode não ver conteúdo autenticado).\n\n${emb.text}`;
          }
          return `Erro web_browse: Chrome logado indisponível e o navegador embutido falhou (${emb.reason}).`;
        }
        return `Erro web_browse: login=true pediu o Chrome logado, mas a ponte chrome-mcp não está disponível. url: ${url}`;
      }

      // public + needs JS — embedded view first (renders JS, app session).
      if (a.browser.embedded && typeof a.browser.embedded.navigate === 'function') {
        const emb = await viaEmbedded(a.browser.embedded, url, extract, selector);
        if (emb.ok) return emb.text;
        // embedded unavailable (e.g. no-window) → fall back to the plain fetch.
        if (a.fetch && typeof a.fetch.url === 'function') {
          const f = await viaFetch(a.fetch, url, extract);
          if (f.ok) return f.text;
          return `Erro web_browse: navegador embutido (${emb.reason}) e fetch (${f.reason}) falharam para ${url}.`;
        }
        return `Erro web_browse: não consegui abrir ${url} (${emb.reason}).`;
      }

      // simplest public — plain fetch.
      if (a.fetch && typeof a.fetch.url === 'function') {
        const f = await viaFetch(a.fetch, url, extract);
        if (f.ok) return f.text;
        return `Erro web_browse: fetch falhou para ${url} (${f.reason}).`;
      }

      return `Erro web_browse: nenhum surface de navegação disponível para ${url}.`;
    } catch (e) {
      return `Erro web_browse: ${(e && e.message) || 'falha ao abrir a web'}`;
    }
  }

  return { def: WEB_BROWSE_DEF, execute, mutating: false };
}
